/**
 * Build-time feature flags (VER-112).
 *
 * Values are injected by the `define` block in vite.config.ts, so they are fixed at build
 * time — there is no runtime toggle and no way for a client to turn one on.
 */

/**
 * Whether the Phase 3 assess PREVIEW surfaces are reachable.
 *
 * These screens (group live check-in, group proxy, the assessment AI panel, the mood and
 * impact previews, and the survey-results analytics) render MOCK wellbeing figures. In
 * production a practitioner could read invented numbers as clinical fact, so they are off.
 * On staging they are the only thing stakeholders can review while the backends are built,
 * so they stay on.
 *
 * `NODE_ENV` deliberately does NOT decide this: `ci/build.yml` sets `NODE_ENV=production`
 * for the staging build as well, so keying off it would blank the previews on staging too.
 * The environment sets `REACT_APP_ASSESS_PREVIEW` explicitly instead.
 *
 * Default when unset: ON for local development, OFF for any built artifact. That way a
 * forgotten variable fails safe (previews hidden) rather than leaking mock data.
 */
export const isAssessPreviewEnabled = (): boolean => {
    const flag = process.env.REACT_APP_ASSESS_PREVIEW;
    if (flag !== undefined && flag !== "") {
        return flag === "1" || flag.toLowerCase() === "true";
    }
    return process.env.NODE_ENV === "development";
};

/**
 * Whether the VER-128 design-preview surfaces are reachable (Create Programme stepper
 * refresh, collection-overview refresh).
 *
 * Distinct from `isAssessPreviewEnabled`: these screens render mock PROGRAMME copy, not
 * wellbeing scores, so there is no clinical-data risk if they leak — but they are still an
 * unfinished design under review, not a shipped flow, so the same fail-closed default
 * applies: ON for local development, OFF for any built artifact unless explicitly set.
 */
export const isDesignPreviewEnabled = (): boolean => {
    const flag = process.env.REACT_APP_DESIGN_PREVIEW;
    if (flag !== undefined && flag !== "") {
        return flag === "1" || flag.toLowerCase() === "true";
    }
    return process.env.NODE_ENV === "development";
};

/**
 * TEMPORARY, STAGING ONLY — whether the survey builder is open to every organisation.
 *
 * The builder is normally Verbal-staff-only (`user.isVerbal`, which is organisation NAME equality
 * in `routes/api/auth.ts`). Staging holds no organisation named "Verbal", so the screen cannot be
 * opened there by any account that exists, and it cannot be reviewed on the environment it is
 * reviewed on. This flag unblocks that and is not a decision about the access model.
 *
 * Unlike the two preview flags above there is NO development default: this one opens an access
 * gate rather than a mock screen, so it is strictly opt-in everywhere and only the exact string
 * "1" (or "true") turns it on. Set to "1" for the staging builds in `pipeline.yml` and to "0" for
 * production. The backend has the matching switch (`SURVEY_BUILDER_OPEN_TO_ALL_ORGS`); without it
 * the screen would open and every save would come back 403.
 */
export const isSurveyBuilderOpenToAllOrgs = (): boolean => {
    const flag = process.env.REACT_APP_SURVEY_BUILDER_OPEN;
    return flag === "1" || flag?.toLowerCase() === "true";
};
